import { directoryVisibilityFilter, utcDate } from '@primum/directory-search';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import algoliasearch from 'algoliasearch';
import { z } from 'zod';

import { THIRTY_MINUTES, ONE_HOUR } from '@/constants/time';
import { cognitoGroupsQueryOptions } from '@/hooks/useCognitoGroups';
import { getUserAttributes } from '@/services/auth';
import { getCancerTypesV2QueryOptions } from '@/services/getCancerTypesV2';
import { getUserAvatarUrlsV2QueryOptions } from '@/services/getUserAvatarUrlsV2';
// The expert search is still read through the legacy services shim, outside any hook that could
// pass the account in. It takes the account as a parameter when the directory screens move.
// eslint-disable-next-line no-restricted-imports
import { store, userIdAtom } from '@/services/globalState';
import { trackDirectorySearchSubmitted } from '@/util/analyticsEvents';
import { env } from '@/util/env';
import { isTestEmail } from '@/util/isTestEmail';
import { isTestUser } from '@/util/isTestUser';
import { mapExpertToProfile, type ExpertProfile } from '@/util/mapDirectoryProfiles';
import { queryClient } from '@/util/queryClient';

const workHistorySchema = z.object({
  endDate: z
    .string()
    .nullish()
    .transform((x) => x ?? undefined),
  accolade: z.string().transform((x) => x ?? undefined),
  isPresent: z
    .boolean()
    .nullish()
    .transform((x) => x ?? undefined),
  startDate: z
    .string()
    .nullish()
    .transform((x) => x ?? undefined),
});

const educationSchema = z.object({
  institution: z
    .string()
    .nullish()
    .transform((x) => x ?? undefined),
  degree: z
    .string()
    .nullish()
    .transform((x) => x ?? undefined),
  studyField: z
    .string()
    .nullish()
    .transform((x) => x ?? undefined),
  startDate: z
    .string()
    .nullish()
    .transform((x) => x ?? undefined),
  endDate: z
    .string()
    .nullish()
    .transform((x) => x ?? undefined),
  isPresent: z
    .boolean()
    .nullish()
    .transform((x) => x ?? undefined),
});

export const disclosureSchema = z.object({
  name: z.string(),
  description: z.string(),
});

const avatarSchema = z.object({
  Location128px: z.string().url().nullish(),
  Bucket: z.string().optional(),
  ETag: z.string().optional(),
  Key: z.string().optional(),
  Location: z.string().url().optional(),
  ServerSideEncryption: z.string().optional(),
  isPublic: z.boolean().nullish(),
});

const locationSchema = z.object({
  city: z.string().nullable(),
  state: z.string().nullable(),
});

const nameSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
});

const highlightResultSchema = z.object({
  Biography: z.unknown(),
  CancerTypeLabel: z.unknown(),
  FocusArea: z.unknown(),
  Institution: z.unknown(),
  Location: z.unknown(),
  Name: z.unknown(),
  Position: z.unknown(),
  Speciality: z.unknown(),
});

const expertSchema = z.object({
  Avatar: avatarSchema.optional(),
  Biography: z.string().nullish(),
  CancerTypeLabel: z.array(z.string()).optional(),
  Disclosures: z.array(disclosureSchema).optional(),
  Education: z.array(educationSchema).optional(),
  FocusArea: z.array(z.string()).optional(),
  Institution: z.string().nullish(),
  Location: locationSchema.nullish(),
  LocationStates: z.array(z.string()).optional(),
  Name: nameSchema.nullish(),
  Position: z.string().nullish(),
  Professional: z.array(workHistorySchema).optional(),
  Speciality: z.string().nullish(),
  _highlightResult: highlightResultSchema.optional(),
  cognitoId: z.string().uuid().optional(),
  focusID: z.array(z.number().positive()).optional(),
  identifiesAsDoctor: z.boolean().optional(),
  objectID: z.string(),
  status: z.enum(['1', '2', '3', '4']).optional(),
  responseTime: z.number().optional(),
  WorkHistory: z.array(workHistorySchema).optional(),
});

const expertsSchema = z.array(expertSchema);

export { expertSchema, expertsSchema };

export type Expert = z.infer<typeof expertSchema>;

export type Name = z.infer<typeof nameSchema>;

export type SearchQuery = string;

type Filters = {
  searchQuery: SearchQuery;
  focusId?: string;
  productTypeId?: string;
  status?: string;
  page?: number;
};

/**
 * Product types and their associated search terms.
 * When a product type is selected, we search for the associated term
 * which leverages Algolia synonyms to find relevant experts.
 *
 * Note that this may be a temporary thing for demo purposes.
 * Long term, we may want to instead add a productTypeId field to the
 * Algolia index. For example:
 *
 * filters: "focusID:42 AND productTypeId:bispecific-antibodies"
 *
 * To do so would require us to add a new productTypeId field on
 * expert records in Algolia though, which would take extra work for
 * now. We'd likely also need to add a productTypeId(s) field to
 * expert users in our DB to have some easy-to-change CRUD for such
 * things.
 *
 * Such an approach would probably be better long term and allow us to
 * separate productTypeId like "Bispecific Antibodies" from specific
 * cancer types.
 */
export const PRODUCT_TYPES = [
  {
    id: 'bispecific-antibodies',
    label: 'Multiple Myeloma / Bispecific Antibodies',
    searchTerm: 'bispecific',
    /**
     * The cancer type, by its value, that restricts the bispecific search term to MM-focused
     * experts. Without this the free-text "bispecific" match surfaces lymphoma/leukemia bios
     * that mention bispecifics like glofitamab or blinatumomab. A value, not an id, because ids
     * are database rows and differ between environments.
     */
    cancerFocusValue: 'multiple-myeloma',
  },
] as const;

export type ProductTypeId = (typeof PRODUCT_TYPES)[number]['id'] | '';

export const getProductTypeById = (id: string) =>
  PRODUCT_TYPES.find((productType) => productType.id === id);

const AWAY_STATUS_KEY = 'status';

const CANCER_FOCUS_ID_KEY = 'focusID';

/**
 * Asking for Active experts means asking for the ones directory search may show, which also
 * leaves out anyone in a scheduled away block today. That rule comes from the shared package so
 * the app and a future backend-issued search key apply the same one.
 */
export const buildAlgoliaFilters = (focusId?: string, status?: string, now: Date = new Date()) => {
  const statusFilter = status ? `${AWAY_STATUS_KEY}:${status}` : '';

  const visibilityFilter = status === '1' ? directoryVisibilityFilter(utcDate(now)) : statusFilter;

  const cancerFocusIdFilter = focusId ? `${CANCER_FOCUS_ID_KEY}:${focusId}` : '';

  const filterString = [visibilityFilter, cancerFocusIdFilter].filter(Boolean).join(' AND ');

  return filterString.length ? filterString : undefined;
};

/**
 * Fields the directory + inquiry flows actually read from each hit. Anything
 * outside this list (notably _highlightResult, LocationStates, CancerTypeLabel)
 * is omitted by Algolia so responses stay small.
 */
const EXPERT_ATTRIBUTES_TO_RETRIEVE = [
  'objectID',
  'Name',
  'Position',
  'Institution',
  'focusID',
  'Avatar',
  'Speciality',
  'identifiesAsDoctor',
  'responseTime',
  'status',
  'Biography',
  'WorkHistory',
  'Education',
  'Disclosures',
  'Professional',
  'Location',
  'cognitoId',
];

const algoliaSearchClient = algoliasearch(
  env.EXPO_PUBLIC_ALGOLIA_APPLICATION_ID,
  env.EXPO_PUBLIC_ALGOLIA_SEARCH_API_KEY
);

const expertsIndex = algoliaSearchClient.initIndex(env.EXPO_PUBLIC_ALGOLIA_INDEX_NAME);

/**
 * Fetches experts from Algolia search index
 *
 * Filters out test experts unless the logged-in user is a test user or in the primum group.
 * Uses cached query data to check if user is in primum group.
 */
const getAlgoliaExperts = async ({
  searchQuery = '',
  focusId = '',
  productTypeId = '',
  status = '',
  page = 0,
  hitsPerPage = 24,
}: Filters & { hitsPerPage?: number }): Promise<{
  hits: ExpertProfile[];
  nbHits: number;
  nbPages: number;
  page: number;
}> => {
  const loggedInUserId = store.get(userIdAtom);

  const { email: loggedInUserEmail } = loggedInUserId
    ? await getUserAttributes()
    : { email: undefined };

  if (!page && searchQuery) {
    trackDirectorySearchSubmitted({ query: searchQuery });
  }

  // If a product type is selected, combine its search term with the user's query
  const productType = productTypeId ? getProductTypeById(productTypeId) : undefined;

  const effectiveSearchQuery = productType
    ? [searchQuery, productType.searchTerm].filter(Boolean).join(' ')
    : searchQuery;

  const filters = buildAlgoliaFilters(focusId, status);

  const searchParams: {
    filters?: string;
    hitsPerPage: number;
    page: number;
    attributesToRetrieve: string[];
    attributesToHighlight: string[];
    analytics: boolean;
    clickAnalytics: boolean;
  } = {
    hitsPerPage,
    page,
    attributesToRetrieve: EXPERT_ATTRIBUTES_TO_RETRIEVE,
    attributesToHighlight: [],
    analytics: false,
    clickAnalytics: false,
  };

  if (filters) {
    searchParams.filters = filters;
  }

  const { hits, nbHits, nbPages } = await expertsIndex.search(effectiveSearchQuery, searchParams);

  if (hits.length === 0) {
    return {
      hits: [],
      nbHits: 0,
      nbPages: 0,
      page,
    };
  }

  const { userAvatarUrlLookup: userSignedUrlLookup } = await queryClient.ensureQueryData(
    getUserAvatarUrlsV2QueryOptions(hits.map((hit) => (hit as Expert).objectID))
  );

  const cancerFocusQuery = await queryClient.ensureQueryData(getCancerTypesV2QueryOptions());

  const groups = await queryClient.ensureQueryData(cognitoGroupsQueryOptions(loggedInUserId));

  const isPrimumUser = Array.isArray(groups) && groups.includes('primum');

  const parsedHits = hits
    .filter((hit: Expert) => {
      const isLoggedInTestUser = isTestEmail(loggedInUserEmail ?? '');

      const fullName = `${hit.Name?.firstName || ''} ${hit.Name?.lastName || ''}`.trim();

      const isTestExpert = isTestUser({
        isTest: hit.Name?.firstName === 'Test',
        name: fullName,
      });

      return isLoggedInTestUser || isPrimumUser || !isTestExpert;
    })
    .map((hit: Expert, index) => {
      const avatar = userSignedUrlLookup.get(Number(hit.objectID));

      const {
        id,
        name,
        lastName,
        title,
        institution,
        bioSections,
        focusArea,
        cancerFocuses,
        cancerFocusesDisplay,
        location,
        avatarSrc,
        initials,
        nameWithoutDoctor,
        biography,
        cognitoId,
        speciality,
        responseTime,
      } = mapExpertToProfile(
        {
          ...hit,
          Avatar: avatar ?? undefined,
        },
        cancerFocusQuery.cancerTypes,
        index
      );

      return {
        id,
        name,
        lastName,
        title,
        institution,
        bioSections,
        focusArea,
        cancerFocuses,
        cancerFocusesDisplay,
        location,
        avatarSrc,
        initials,
        nameWithoutDoctor,
        biography,
        cognitoId,
        speciality,
        responseTime,
      };
    });

  return {
    hits: parsedHits,
    nbHits,
    nbPages,
    page,
  };
};

/**
 * Fetches a single expert by Algolia objectID. One Algolia op, one record —
 * use this instead of paginating through the full index just to resolve one
 * expert by id.
 */
const getAlgoliaExpertById = async (id: string): Promise<ExpertProfile | null> => {
  if (!id) {
    return null;
  }

  const record = await expertsIndex
    .getObject<Expert>(id, { attributesToRetrieve: EXPERT_ATTRIBUTES_TO_RETRIEVE })
    .catch((error: unknown) => {
      const status = (error as { status?: number })?.status;

      if (status === 404) {
        return null;
      }

      throw error;
    });

  if (!record) {
    return null;
  }

  const [cancerFocusQuery, avatarLookup] = await Promise.all([
    queryClient.ensureQueryData(getCancerTypesV2QueryOptions()),
    queryClient.ensureQueryData(getUserAvatarUrlsV2QueryOptions([id])),
  ]);

  const signedAvatar = avatarLookup.userAvatarUrlLookup.get(Number(id));

  return mapExpertToProfile(
    { ...record, Avatar: signedAvatar ?? record.Avatar },
    cancerFocusQuery.cancerTypes
  );
};

export const getAlgoliaExpertByIdQueryOptions = (id?: string | number | null) => {
  const normalizedId = String(id ?? '');

  return queryOptions({
    queryKey: ['algolia', 'expertById', normalizedId],
    queryFn: () => getAlgoliaExpertById(normalizedId),
    enabled: normalizedId.length > 0,
    staleTime: THIRTY_MINUTES,
    gcTime: ONE_HOUR,
  });
};

/**
 * `maxPages` caps the cancer-focus picker (and similar non-search callers) at
 * a single 100-hit page so opening the picker can never silently auto-page
 * through Algolia. Search-driven callers use the default (unset) and continue
 * to paginate via onEndReached.
 */
type GetAlgoliaExpertsInfiniteOptions = {
  maxPages?: number;
  hitsPerPage?: number;
};

export const getAlgoliaExpertsInfiniteQueryOptions = (
  filters: Filters = {
    searchQuery: '',
    focusId: '',
    productTypeId: '',
  },
  { maxPages, hitsPerPage }: GetAlgoliaExpertsInfiniteOptions = {}
) =>
  infiniteQueryOptions({
    queryKey: [
      'algolia',
      'experts',
      {
        searchQuery: filters.searchQuery,
        focusId: filters.focusId,
        productTypeId: filters.productTypeId,
        hitsPerPage,
        maxPages,
      },
    ],
    queryFn: ({ pageParam = 0 }) =>
      getAlgoliaExperts({ ...filters, page: pageParam, status: '1', hitsPerPage }),
    staleTime: THIRTY_MINUTES,
    gcTime: ONE_HOUR,
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, _lastPageParam, allPageParams) => {
      const hasMore = lastPage.page + 1 < lastPage.nbPages;

      if (!hasMore) {
        return undefined;
      }

      if (maxPages != null && allPageParams.length >= maxPages) {
        return undefined;
      }

      return lastPage.page + 1;
    },
    retry: false,
  });
